import React from 'react';

import { translate } from '~l10n/client';

import { Action } from '~shared/components/ActionMenu';
import { Container } from '~shared/components/Container';
import { Sort } from '~shared/components/Table/update/components/Sort';
import { useSocTable } from '~shared/components/Table/update/useSocTable';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { WorkspaceMembersTableAccessMenu } from '~teacher/components/workspaces/WorkspaceMembers/WorkspaceMembersTable/WorkspaceMembersTableAccessMenu';
import { WorkspaceMembersTableCell } from '~teacher/components/workspaces/WorkspaceMembers/WorkspaceMembersTable/WorkspaceMembersTableCell';
import { WorkspaceMembersTableIncrementMenu } from '~teacher/components/workspaces/WorkspaceMembers/WorkspaceMembersTable/WorkspaceMembersTableIncrementMenu';
import { WorkspaceMemberTableRow } from '~teacher/components/workspaces/WorkspaceMembers/WorkspaceMembersTable/WorkspaceMemberTableRow';
import { useWorkspacePermissions } from '~teacher/hook/workspaces';
import { useGetWorkspaceRole } from '~teacher/hook/workspaces/useGetWorkspaceRole';

import {
  WorkspaceMember,
  WorkspaceRole,
} from '../../../../../api/workspaces/getWorkspaceMembers';
import { assertUnreachable } from '../../../../../util/assertUnreachable';
import { WorkspaceMembersTableWrapper } from './WorkspaceMembersTableWrapper';

interface WorkspaceMemberSortingRule {
  id: keyof WorkspaceMember & string;
  desc: boolean;
}

interface Props {
  data: Array<WorkspaceMember>;
  initialSort: WorkspaceMemberSortingRule;
  children?: React.ReactNode;
  emptyState: React.ReactNode;
  actions: Action<WorkspaceMember>[];
  workspaceId: string;
}

const workspaceMembersColumns = [
  {
    Header: translate('Name'),
    id: 'fullName',
    accessor: (item) => {
      const displayName = item.fullName || translate('Pending login');
      return item.isYou
        ? translate('{0} (You)').format(item.fullName)
        : displayName;
    },
    sortBy: 'alphanumeric',
  },
  {
    Header: translate('Email'),
    id: 'email',
    accessor: 'email',
    sortBy: 'alphanumeric',
  },
  {
    Header: translate('Access Level'),
    accessor: (item) => item.role,
    id: 'access',
    Cell: ({ value }: { value: WorkspaceRole }) => {
      switch (value) {
        case WorkspaceRole.Owner:
          return translate('Owner');
        case WorkspaceRole.Editor:
          return translate('Editor');
        case WorkspaceRole.Viewer:
          return translate('Viewer');
        default:
          // ew
          assertUnreachable(value);
      }
    },
  },
];

export function WorkspaceMembersTable({
  data,
  children,
  emptyState,
  actions,
  workspaceId,
}: Props) {
  const applicationContext = useTeacherApplicationContext();

  // Filter out email column for Showbie context
  const columns = React.useMemo(
    () =>
      applicationContext === TeacherApplicationContext.ShowbieAssessments
        ? workspaceMembersColumns.filter((col) => col.id !== 'email')
        : workspaceMembersColumns,
    [applicationContext]
  );

  // sort that prevails no matter local sorts (like modified, etc.)
  // hook initializer
  const {
    resolvedData,
    incrementFn,
    prepareRow,
    maxRows,
    selectedRows,
    totalRows,
    props: { headerGroups },
  } = useSocTable({
    data,
    // initialSort,
    columns,
    enableBulkActions: true,
    increment: 50,
    sortable: true,
  });

  const currentUser = useGetWorkspaceRole({ workspaceId });
  const { permissions } = useWorkspacePermissions(workspaceId);

  if (data.length === 0) return <></>;

  return (
    <Container className={'relative'}>
      {children}
      <WorkspaceMembersTableWrapper>
        {/* header row */}
        {headerGroups.map((headerGroup) => {
          return (
            <WorkspaceMemberTableRow
              selectedRows={selectedRows}
              key={headerGroup.getHeaderGroupProps().key}
              header
            >
              {headerGroup.headers.map((column) => {
                return (
                  <WorkspaceMembersTableCell
                    key={column.getHeaderProps().key}
                    header
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    column={column}
                  >
                    {column.id !== 'actions' && column.render('Header')}
                    <Sort
                      enabled={
                        column.canSort &&
                        column.isSorted &&
                        !column.doNotShowSort
                      }
                      descending={column.isSortedDesc}
                    />
                  </WorkspaceMembersTableCell>
                );
              })}
            </WorkspaceMemberTableRow>
          );
        })}
        {/* body rows */}
        {resolvedData.map((row) => {
          prepareRow(row);
          return (
            <WorkspaceMemberTableRow
              row={row}
              key={`action-${row.getRowProps().key}`}
              selectedRows={selectedRows}
            >
              {row.cells.map((cell) =>
                cell.column.id === 'access' ? (
                  <WorkspaceMembersTableAccessMenu
                    key={`access-${row.getRowProps().key}`}
                    currentUserRole={currentUser.data?.role}
                    row={row}
                    cell={cell}
                    actions={actions}
                    canRemoveOthers={permissions.removeOthers}
                    currentUserId={currentUser.data?.id}
                  />
                ) : (
                  <WorkspaceMembersTableCell
                    key={cell.getCellProps().key}
                    column={cell.column}
                  >
                    {cell.render('Cell')}
                  </WorkspaceMembersTableCell>
                )
              )}
            </WorkspaceMemberTableRow>
          );
        })}
      </WorkspaceMembersTableWrapper>
      <WorkspaceMembersTableIncrementMenu
        maxRows={maxRows}
        setMaxRows={incrementFn}
        totalRows={totalRows}
      />
      {resolvedData.length === 0 ? emptyState : null}
    </Container>
  );
}
